import { defineStore } from 'pinia';
import { Apii } from '@/services/api.js';
import { notify } from 'notiwind';

const ApiService = new Apii();

export const usePointsStore = defineStore('points', {
    state: () => ({
        loading: false,
        reward: {},
        user: {},
        addRewardUser: {},
        programs: [],
        rewards: {
            users: [],
        },
        cppData: {},
        cppThreshold: 40,
        saveProgramBtnAction: false,
        showProgramModal: false,
        showEditProgramModal: false,
        showBulkUpdateModal: false,
        bulkUpdateUserId: null,
        showProgramUserOptionsModal: false,
        filteredUserPoints: [],
        // Points history sidebar state
        showPointsHistorySidebar: false,
        selectedProgramForHistory: null,
        selectedUserForHistory: null,
        pointsHistoryData: {
            labels: [],
            datasets: [],
            records: [],
            program_name: '',
            total: 0,
            has_more: false,
            cards: [],
        },
        loadingPointsHistory: false,
        pointsHistoryOffset: 0,
        // Best transfer rates state
        bestTransferRates: [],
        loadingBestTransferRates: false,
    }),
    actions: {
        async getRewards(disableCache = false) {
            this.loading = true;
            try {
                const response = await ApiService.apiCall(
                    '/rewards?disable_cache=' + disableCache,
                    'get'
                );
                this.rewards = response.data.rewards || { users: [] };
                this.programs = response.data.programs || [];
                this.cppData = response.data.cpp_data || {};
                this.cppThreshold = response.data.cpp_threshold || 40;
                setTimeout(() => {
                    this.loading = false;
                }, 500);
            } catch {
                this.loading = false;
                // Ensure rewards is never null even on error
                this.rewards = { users: [] };
                this.programs = [];
                this.cppData = {};
            }
        },
        editProgram(reward, user) {
            this.reward = reward;
            this.user = user;
            this.showEditProgramModal = true;
        },
        openBulkUpdateModal(userId) {
            this.bulkUpdateUserId = userId ? Number(userId) : null;
            this.showBulkUpdateModal = true;
        },
        closeBulkUpdateModal() {
            this.showBulkUpdateModal = false;
            this.bulkUpdateUserId = null;
        },
        deleteProgram(reward) {
            return ApiService.apiCall(
                '/rewards/' + reward.manual_reward_id,
                'delete'
            )
                .then(() => {
                    this.getRewards();
                    notify({
                        group: 'success',
                        title: 'Program deleted',
                        text: 'Your program points have been deleted.',
                    });
                })
                .catch((error) => {
                    this.loading = false;
                    notify({
                        group: 'error',
                        title: 'Delete failed',
                        text: 'There was an error deleting your program points. Please try again.',
                    });
                    throw error;
                });
        },
        setProgramModal(data) {
            this.showProgramModal = data;
        },
        setEditProgramModal(data) {
            this.showEditProgramModal = data;
        },
        showProgramModalAction(data) {
            this.addRewardUser = data;
            this.showProgramModal = true;
        },
        setShowProgramModal(val) {
            this.showProgramUserOptionsModal = val;
        },
        editProgramOptions(program, user) {
            this.reward = program;
            this.user = user;
            this.showProgramUserOptionsModal = true;
        },
        setRewardsOrigin() {
            // Handle case where program_user might not have an id yet (new program_user)
            const method = this.reward.program_user.id ? 'put' : 'post';
            const url = this.reward.program_user.id
                ? '/program-user/' + this.reward.program_user.id + '/options'
                : '/program-user';

            ApiService.apiCall(url, method, this.reward.program_user)
                .then(() => {
                    this.getRewards();
                })
                .catch((error) => {
                    console.error(error);
                });
        },

        filterDataByProgramId(programId) {
            // console.log(this.rewards.users);
            // console.log(programId);
            if (!this.rewards?.users) {
                return [];
            }
            const filteredUsersPoints = this.rewards.users.reduce(
                (result, user) => {
                    const matchingPoints = user.programs.find(
                        (program) => program.id === programId
                    );
                    result.push({
                        userName: user.name,
                        userId: user.id,
                        points: matchingPoints ? matchingPoints.points : 0,
                    });
                    return result;
                },
                []
            );
            return filteredUsersPoints;
        },
        filterUserPoints(transferPartnersArray) {
            // param transferPartnersArray = transfer partners for specific program
            // returns users' points for each TP in transferPartnersArray
            if (!this.rewards?.users) {
                this.filteredUserPoints = [];
                return;
            }
            this.filteredUserPoints = transferPartnersArray
                .map((partner) => {
                    const usersWithProgram = this.rewards.users
                        .filter((user) =>
                            user.programs.some(
                                (program) =>
                                    program.id === partner.source_program_id
                            )
                        )
                        .map((user) => ({
                            userName: user.name,
                            points: user.programs.find(
                                (program) =>
                                    program.id === partner.source_program_id
                            )?.points,
                        }));

                    if (usersWithProgram.length) {
                        const bonusExpiration = partner.bonus_expiration
                            ? partner.bonus_expiration
                            : null;

                        return {
                            programName: partner.source_program_name,
                            image: partner.source_program.image,
                            transferRateFrom: Number(
                                partner.transfer_rate_from
                            ),
                            transferRateTo: Number(partner.transfer_rate_to),
                            transferTime: partner.transfer_time,
                            bonusExpiration: bonusExpiration,
                            bonusRateFrom: Number(partner.bonus_rate_from),
                            bonusRateTo: Number(partner.bonus_rate_to),
                            points: usersWithProgram,
                        };
                    }

                    return null;
                })
                .filter((filteredPoint) => filteredPoint !== null);
        },

        // Points history sidebar actions
        openPointsHistorySidebar(program, user) {
            this.selectedProgramForHistory = program;
            this.selectedUserForHistory = user;
            this.showPointsHistorySidebar = true;
            this.pointsHistoryOffset = 0;
            this.fetchPointsHistory();
            this.fetchBestTransferRates();
        },

        closePointsHistorySidebar() {
            this.showPointsHistorySidebar = false;
            this.selectedProgramForHistory = null;
            this.selectedUserForHistory = null;
            this.pointsHistoryOffset = 0;
            this.pointsHistoryData = {
                labels: [],
                datasets: [],
                program_name: '',
                total: 0,
                has_more: false,
                cards: [],
            };
            this.bestTransferRates = [];
        },

        async fetchPointsHistory(period = 'all_time', append = false) {
            if (
                !this.selectedProgramForHistory ||
                !this.selectedUserForHistory
            ) {
                return;
            }

            this.loadingPointsHistory = true;

            try {
                const isConnected = this.selectedProgramForHistory.connected
                    ? 1
                    : 0;
                const response = await ApiService.apiCall(
                    `/rewards/history/${this.selectedProgramForHistory.id}/${this.selectedUserForHistory.id}?period=${period}&limit=50&offset=${this.pointsHistoryOffset}&connected=${isConnected}`,
                    'get'
                );

                if (append && this.pointsHistoryData.labels.length > 0) {
                    // Append new data to existing
                    this.pointsHistoryData = {
                        labels: [
                            ...this.pointsHistoryData.labels,
                            ...response.data.labels,
                        ],
                        datasets: [
                            ...this.pointsHistoryData.datasets,
                            ...response.data.datasets,
                        ],
                        records: [
                            ...(this.pointsHistoryData.records || []),
                            ...(response.data.records || []),
                        ],
                        program_name: response.data.program_name,
                        total: response.data.total,
                        has_more: response.data.has_more,
                    };
                } else {
                    this.pointsHistoryData = response.data;
                }
            } catch (error) {
                console.error('Error fetching points history:', error);
                this.pointsHistoryData = {
                    labels: [],
                    datasets: [],
                    records: [],
                    program_name: '',
                    total: 0,
                    has_more: false,
                    cards: [],
                };
            } finally {
                this.loadingPointsHistory = false;
            }
        },

        async loadMorePointsHistory(period = 'all_time') {
            this.pointsHistoryOffset += 50;
            await this.fetchPointsHistory(period, true);
        },

        async goToPreviousHistoryPage(period = 'all_time') {
            this.pointsHistoryOffset = Math.max(
                0,
                this.pointsHistoryOffset - 50
            );
            await this.fetchPointsHistory(period, false);
        },

        async updateHistoryEntry(entryId, balance, balanceDate) {
            try {
                await ApiService.apiCall(`/rewards/history/${entryId}`, 'put', {
                    balance,
                    balance_date: balanceDate,
                });
                notify({
                    group: 'success',
                    title: 'Entry updated',
                    text: 'Points history entry has been updated.',
                });
                // Refresh history data and main rewards data
                await this.refreshAfterHistoryChange();
                return true;
            } catch (error) {
                console.error('Error updating history entry:', error);
                notify({
                    group: 'error',
                    title: 'Update failed',
                    text: 'There was an error updating the entry. Please try again.',
                });
                return false;
            }
        },

        async deleteHistoryEntry(entryId) {
            try {
                await ApiService.apiCall(
                    `/rewards/history/${entryId}`,
                    'delete'
                );
                notify({
                    group: 'success',
                    title: 'Entry deleted',
                    text: 'Points history entry has been deleted.',
                });
                // Refresh history data and main rewards data
                await this.refreshAfterHistoryChange();
                return true;
            } catch (error) {
                console.error('Error deleting history entry:', error);
                notify({
                    group: 'error',
                    title: 'Delete failed',
                    text: 'There was an error deleting the entry. Please try again.',
                });
                return false;
            }
        },

        async refreshAfterHistoryChange() {
            // Store the current program/user IDs before refresh
            const programId = this.selectedProgramForHistory?.id;
            const userId = this.selectedUserForHistory?.id;

            // Refresh main rewards data first and wait for it (with cache disabled)
            await this.getRewards(true);

            // Update selectedProgramForHistory with fresh data from rewards
            if (programId && userId && this.rewards?.users) {
                const user = this.rewards.users.find((u) => u.id === userId);
                if (user) {
                    const freshProgram = user.programs.find(
                        (p) => p.id === programId
                    );
                    if (freshProgram) {
                        this.selectedProgramForHistory = freshProgram;
                    }
                }
            }

            // Refresh history data after rewards are updated
            this.pointsHistoryOffset = 0;
            await this.fetchPointsHistory();
        },

        async fetchBestTransferRates() {
            if (!this.selectedProgramForHistory) {
                return;
            }

            this.loadingBestTransferRates = true;

            try {
                const response = await ApiService.apiCall(
                    `/programs/${this.selectedProgramForHistory.id}/best-transfer-rates`,
                    'get'
                );

                this.bestTransferRates =
                    response.data.best_transfer_rates || [];
            } catch (error) {
                console.error('Error fetching best transfer rates:', error);
                this.bestTransferRates = [];
            } finally {
                this.loadingBestTransferRates = false;
            }
        },
    },
});
